import { DatabaseIcon } from "lucide-react";
import React, { ReactNode, useMemo } from "react";

import { Citations } from "../Citations";
import { CopyButton } from "../ui/copy-button";

import CodeAccordion from "@/components/CodeAccordion";
import { BasicTooltip } from "@/components/ui/Tooltip";
import McpAccordion from "@/components/McpAccordion";
import ThinkingAccordion from "@/components/ThinkingAccordion";
import { MediaBlock, OutputMediaBlock } from "@/components/ui/MediaBlocks";
import {
  BashCodeExecutionToolResultContent,
  CodeExecutionResultContent,
  Media,
  Message,
  ServerToolUseContent,
  TextEditorCodeExecutionToolResultContent,
  WebSearchToolResultContent,
} from "@/types";
import type { CacheControl, OutputMediaContent } from "@/types/prompt-blueprint";
import { detectMediaTypeFromUrl, getMediaTitle, isSingleTextBlock } from "@/utils/media";
import { truncate } from "@/utils/strings";
import { formatMcpError } from "@/utils/utils";

/**
 * Text content block with hover copy button overlay
 */
function getCacheLabel(cc: CacheControl | undefined): string | undefined {
  if (!cc) {
    return undefined;
  }
  return cc.ttl === "1h" ? "1h" : "5m";
}

const TextContentBlock = ({
  content,
  highlight,
  highlightedContent,
  isSingleBlock,
  cacheControl,
}: {
  content: string;
  highlight: boolean;
  highlightedContent: string | React.ReactNode | null;
  isSingleBlock: boolean;
  cacheControl?: CacheControl;
}) => {
  const isJson = (() => {
    try {
      JSON.parse(content);
      return true;
    } catch {
      return false;
    }
  })();

  const displayContent = isSingleBlock && highlightedContent ? highlightedContent : content;
  const cacheLabel = getCacheLabel(cacheControl);

  const textElement = isJson ? (
    <pre className="whitespace-pre-wrap">{JSON.stringify(JSON.parse(content), null, 2)}</pre>
  ) : (
    <span className={`${highlight ? "bg-blue-100" : ""} overflow-x-auto whitespace-pre-wrap`}>
      {displayContent}
    </span>
  );

  return (
    <div className="group/textblock relative rounded-lg p-3 group-hover:bg-gray-50">
      {textElement}
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover/textblock:opacity-100">
        {cacheLabel && (
          <BasicTooltip content="Prompt cache breakpoint">
            <span className="flex items-center gap-0.5 rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
              <DatabaseIcon className="h-3 w-3" />
              {cacheLabel}
            </span>
          </BasicTooltip>
        )}
        <CopyButton text={content} />
      </div>
    </div>
  );
};

export const NotAction = ({
  componentIsOpen,
  highlight,
  highlightedContent,
  message,
  allMessages,
}: {
  componentIsOpen: boolean;
  highlight: boolean;
  highlightedContent: string | React.ReactNode | null;
  message?: Message;
  allMessages?: Message[];
}) => {
  const { contentBlocks, isMalformed } = useMemo(() => {
    if (!message?.content) {
      return { contentBlocks: [], isMalformed: false };
    }

    if (!Array.isArray(message.content)) {
      return { contentBlocks: [], isMalformed: true };
    }

    const blocks: ReactNode[] = [];
    const hasSingleTextBlock = isSingleTextBlock(message.content);

    // Render each content block in order (preserving original sequence)
    message.content.forEach((content, index) => {
      if (content.type === "thinking") {
        blocks.push(
          <ThinkingAccordion
            key={`thinking.${content.thinking}`}
            value={content.thinking}
            id={index}
            className="mb-4"
          />,
        );
      } else if (content.type === "code") {
        blocks.push(
          <CodeAccordion
            key={`code.${content.id || index}`}
            code={content.code}
            id={content.id || `code-${index}`}
            containerId={content.container_id}
            className="mb-4"
          />,
        );
      } else if (content.type === "code_execution_result") {
        const resultBlock = content as CodeExecutionResultContent;
        blocks.push(
          <div
            key={`code-result.${resultBlock.output.slice(0, 40)}`}
            className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700"
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Output
            </div>
            <pre className="whitespace-pre-wrap break-words">{resultBlock.output}</pre>
          </div>,
        );
      } else if (content.type === "server_tool_use") {
        const toolBlock = content as ServerToolUseContent;
        const formatted = Object.entries(toolBlock.input)
          .map(
            ([key, value]) =>
              `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`,
          )
          .join("\n");
        blocks.push(
          <div
            key={`server-tool.${toolBlock.id}`}
            className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700"
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              {toolBlock.name}
            </div>
            <pre className="whitespace-pre-wrap break-words">{formatted}</pre>
          </div>,
        );
      } else if (content.type === "bash_code_execution_tool_result") {
        const resultBlock = content as BashCodeExecutionToolResultContent;
        const inner = resultBlock.content;
        const isError = inner.type?.endsWith("_error");
        blocks.push(
          <div
            key={`bash-result.${resultBlock.tool_use_id}`}
            className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700"
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Bash Result
            </div>
            {isError ? (
              <pre className="whitespace-pre-wrap break-words text-red-600">
                {inner.error_code}: {inner.error_message}
              </pre>
            ) : (
              <pre className="whitespace-pre-wrap break-words">
                {inner.stdout}
                {inner.stderr ? `\nSTDERR: ${inner.stderr}` : ""}
              </pre>
            )}
          </div>,
        );
      } else if (content.type === "text_editor_code_execution_tool_result") {
        const resultBlock = content as TextEditorCodeExecutionToolResultContent;
        const inner = resultBlock.content;
        const isError = inner.type?.endsWith("_error");
        let body: React.ReactNode;
        if (isError) {
          body = (
            <pre className="whitespace-pre-wrap break-words text-red-600">
              {inner.error_code}: {inner.error_message}
            </pre>
          );
        } else if (inner.type?.includes("view")) {
          body = <pre className="whitespace-pre-wrap break-words">{inner.content}</pre>;
        } else if (inner.type?.includes("str_replace")) {
          body = <pre className="whitespace-pre-wrap break-words">{inner.lines?.join("\n")}</pre>;
        } else if (inner.type?.includes("create")) {
          body = (
            <span className="text-gray-500">
              {inner.is_file_update ? "File updated" : "File created"}
            </span>
          );
        } else {
          body = (
            <pre className="whitespace-pre-wrap break-words">{JSON.stringify(inner, null, 2)}</pre>
          );
        }
        blocks.push(
          <div
            key={`text-editor-result.${resultBlock.tool_use_id}`}
            className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700"
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Text Editor Result
            </div>
            {body}
          </div>,
        );
      } else if (componentIsOpen && content.type === "media") {
        const rawMedia = content.media;
        const detectedType = detectMediaTypeFromUrl(rawMedia.url, rawMedia.type);

        const normalizedMedia: Media = {
          ...rawMedia,
          type: detectedType,
          title: getMediaTitle(rawMedia.url, rawMedia.title, detectedType),
        };
        blocks.push(
          <MediaBlock key={`media.${content.media.url}`} media={normalizedMedia} readonly />,
        );
      } else if (componentIsOpen && content.type === "image_url") {
        const imageUrl = content.image_url?.url;
        if (imageUrl) {
          const detectedType = detectMediaTypeFromUrl(imageUrl, "image");
          const normalizedMedia: Media = {
            type: detectedType,
            url: imageUrl,
            title: getMediaTitle(imageUrl, content.image_variable, detectedType),
          };
          blocks.push(
            <MediaBlock key={`image_url.${imageUrl}`} media={normalizedMedia} readonly />,
          );
        }
      } else if (componentIsOpen && content.type === "output_media") {
        const mediaContent = content as OutputMediaContent & {
          revised_prompt?: string;
        };
        if (mediaContent.url) {
          blocks.push(
            <OutputMediaBlock
              key={`output_media.${mediaContent.url.slice(0, 50)}`}
              content={{
                type: "output_media",
                id: mediaContent.id,
                url: mediaContent.url,
                mime_type: mediaContent.mime_type,
                media_type: mediaContent.media_type ?? "image",
                provider_metadata: {
                  ...mediaContent.provider_metadata,
                  revised_prompt:
                    mediaContent.provider_metadata?.revised_prompt ?? mediaContent.revised_prompt,
                },
              }}
              readonly
            />,
          );
        }
      } else if (content.type === "mcp_list_tools") {
        const mcpBlock = content as {
          server_label: string;
          tools: {
            name: string;
            description?: string;
            input_schema?: Record<string, unknown>;
            annotations?: Record<string, unknown>;
          }[];
          error?: string | null;
        };
        const toolCount = mcpBlock.tools.length;
        const toolWord = toolCount === 1 ? "tool" : "tools";
        blocks.push(
          <McpAccordion
            key={`mcp-list.${mcpBlock.server_label}`}
            id={`mcp-list-${mcpBlock.server_label}`}
            title={`${toolCount} ${toolWord} available`}
            serverLabel={mcpBlock.server_label}
            variant={mcpBlock.error ? "error" : "default"}
            className="mb-4"
          >
            <div className="px-4 py-3">
              {mcpBlock.error ? (
                <pre className="whitespace-pre-wrap break-words text-sm text-red-600">
                  {formatMcpError(mcpBlock.error)}
                </pre>
              ) : (
                <div className="flex flex-col divide-y divide-gray-100">
                  {mcpBlock.tools.map(tool => {
                    const schema = tool.input_schema as
                      | {
                          properties?: Record<string, { type?: string; description?: string }>;
                          required?: string[];
                        }
                      | undefined;
                    const params = schema?.properties ? Object.entries(schema.properties) : [];
                    const requiredParams = schema?.required ?? [];
                    return (
                      <div key={`tool-${tool.name}`} className="py-3 first:pt-0 last:pb-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-gray-900">
                            {tool.name}
                          </span>
                          {tool.annotations?.read_only === true && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                              read-only
                            </span>
                          )}
                        </div>
                        {tool.description && (
                          <p className="mt-1 text-xs leading-relaxed text-gray-500">
                            {tool.description}
                          </p>
                        )}
                        {params.length > 0 && (
                          <div className="mt-2 rounded-md border border-gray-100 bg-white/60">
                            <div className="px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                              Parameters
                            </div>
                            <div className="divide-y divide-gray-100">
                              {params.map(([paramName, paramDef]) => (
                                <div
                                  key={paramName}
                                  className="flex items-center gap-2 px-2.5 py-1.5"
                                >
                                  <code className="shrink-0 text-xs font-medium text-gray-700">
                                    {paramName}
                                  </code>
                                  {paramDef.type && (
                                    <span className="shrink-0 rounded bg-blue-50 px-1 py-0.5 text-[10px] font-medium text-blue-600">
                                      {paramDef.type}
                                    </span>
                                  )}
                                  {requiredParams.includes(paramName) && (
                                    <span className="shrink-0 text-[10px] font-medium text-red-400">
                                      required
                                    </span>
                                  )}
                                  {paramDef.description && (
                                    <span className="text-xs text-gray-400">
                                      {paramDef.description}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </McpAccordion>,
        );
      } else if (content.type === "mcp_call") {
        const mcpCall = content as {
          name: string;
          server_label: string;
          arguments: string;
          output?: string | null;
          error?: string | Record<string, unknown> | null;
        };
        const hasError = Boolean(mcpCall.error);
        const errorStr = formatMcpError(mcpCall.error);
        const copyValue = errorStr || mcpCall.output || "";

        let parsedArgs: Record<string, unknown> | null = null;
        if (mcpCall.arguments) {
          try {
            parsedArgs = JSON.parse(mcpCall.arguments);
          } catch {
            /* keep as raw string */
          }
        }

        blocks.push(
          <McpAccordion
            key={`mcp-call.${mcpCall.name}.${mcpCall.arguments.slice(0, 30)}`}
            id={`mcp-call-${mcpCall.name}`}
            title={mcpCall.name}
            serverLabel={mcpCall.server_label}
            variant={hasError ? "error" : "default"}
            copyValue={copyValue}
            className="mb-4"
          >
            <div className="flex flex-col gap-3 px-4 py-3">
              {mcpCall.arguments && (
                <div>
                  <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                    Arguments
                  </div>
                  {parsedArgs ? (
                    <div className="divide-y divide-gray-100 rounded-md border border-gray-100 bg-white/60">
                      {Object.entries(parsedArgs).map(([key, val]) => (
                        <div key={key} className="flex flex-col gap-0.5 px-2.5 py-2">
                          <code className="text-xs font-semibold text-gray-600">{key}</code>
                          <span className="break-words rounded bg-gray-50 px-1.5 py-1 font-mono text-xs text-gray-500">
                            {typeof val === "string" ? `"${val}"` : JSON.stringify(val, null, 2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="whitespace-pre-wrap break-words rounded-md border border-gray-100 bg-white/60 p-2.5 font-mono text-xs text-gray-600">
                      {mcpCall.arguments}
                    </pre>
                  )}
                </div>
              )}
              {hasError && (
                <div>
                  <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-red-400">
                    Error
                  </div>
                  <pre className="whitespace-pre-wrap break-words rounded-md border border-red-100 bg-red-50/50 p-2.5 font-mono text-xs text-red-600">
                    {errorStr}
                  </pre>
                </div>
              )}
              {!hasError && mcpCall.output && (
                <div>
                  <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                    Response
                  </div>
                  <div className="max-h-48 overflow-y-auto rounded-md border border-gray-100 bg-white/60 p-2.5">
                    <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-700">
                      {mcpCall.output}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </McpAccordion>,
        );
      } else if (content.type === "mcp_approval_request") {
        const approval = content as {
          id?: string;
          name: string;
          arguments: string;
          server_label: string;
        };
        const title = `Approval: ${approval.name}`;

        let parsedArgs: Record<string, unknown> | null = null;
        if (approval.arguments) {
          try {
            parsedArgs = JSON.parse(approval.arguments);
          } catch {
            /* keep as raw string */
          }
        }

        const isResolved =
          approval.id &&
          (allMessages ?? []).some(
            m =>
              Array.isArray(m.content) &&
              m.content.some(
                c =>
                  c.type === "mcp_approval_response" &&
                  (c as { approval_request_id?: string }).approval_request_id === approval.id,
              ),
          );

        const hasArgs =
          approval.arguments &&
          approval.arguments !== "{}" &&
          (!parsedArgs || Object.keys(parsedArgs).length > 0);
        const hasContent = hasArgs || !isResolved;

        blocks.push(
          <McpAccordion
            key={`mcp-approval.${approval.name}.${approval.arguments.slice(0, 30)}`}
            id={`mcp-approval-${approval.name}`}
            title={title}
            serverLabel={approval.server_label}
            defaultOpen={hasContent ? componentIsOpen : false}
            variant="default"
          >
            {hasContent && (
              <div className="flex flex-col gap-3 px-4 py-3">
                {hasArgs && (
                  <div>
                    <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                      Arguments
                    </div>
                    {parsedArgs ? (
                      <div className="divide-y divide-gray-100 rounded-md border border-gray-100 bg-white/60">
                        {Object.entries(parsedArgs).map(([key, val]) => (
                          <div key={key} className="flex flex-col gap-0.5 px-2.5 py-2">
                            <code className="text-xs font-semibold text-gray-600">{key}</code>
                            <span className="break-words rounded bg-gray-50 px-1.5 py-1 font-mono text-xs text-gray-500">
                              {typeof val === "string" ? `"${val}"` : JSON.stringify(val, null, 2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className="whitespace-pre-wrap break-words rounded-md border border-gray-100 bg-white/60 p-2.5 font-mono text-xs text-gray-600">
                        {approval.arguments}
                      </pre>
                    )}
                  </div>
                )}
                {!isResolved && (
                  <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                    <span className="text-xs font-medium text-amber-700">Awaiting Approval</span>
                  </div>
                )}
              </div>
            )}
          </McpAccordion>,
        );
      } else if (content.type === "mcp_approval_response") {
        const response = content as {
          approval_request_id: string;
          approve: boolean;
        };
        const isApproved = response.approve;
        blocks.push(
          <div
            key={`mcp-approval-response-${response.approval_request_id}`}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
              isApproved ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"
            }`}
          >
            <div className={`h-2 w-2 rounded-full ${isApproved ? "bg-green-400" : "bg-red-400"}`} />
            <span
              className={`text-xs font-medium ${isApproved ? "text-green-700" : "text-red-700"}`}
            >
              {isApproved ? "Approved" : "Denied"}
            </span>
          </div>,
        );
      } else if (content.type === "web_search_tool_result") {
        const searchBlock = content as WebSearchToolResultContent;
        const results = searchBlock.content?.filter(r => r.type === "web_search_result");
        if (results?.length) {
          blocks.push(
            <div
              key={`web-search-result.${searchBlock.tool_use_id}`}
              className="mb-4 flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-3"
            >
              <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                Search Results
              </div>
              {results.map(result => (
                <a
                  key={result.url}
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {result.title}
                </a>
              ))}
            </div>,
          );
        }
      } else if (content.type === "shell_call") {
        const shellBlock = content as {
          id?: string;
          call_id?: string;
          action: { commands?: string[]; timeout_ms?: number };
          status?: string;
        };
        const commands = shellBlock.action?.commands || [];
        blocks.push(
          <CodeAccordion
            key={`shell.${shellBlock.id || index}`}
            code={commands.join("\n")}
            id={shellBlock.id || `shell-${index}`}
            label="Shell"
            className="mb-4"
          />,
        );
      } else if (content.type === "shell_call_output") {
        const shellOutput = content as {
          id?: string;
          call_id?: string;
          output: {
            stdout: string;
            stderr: string;
            outcome: { type: string; exit_code?: number };
          }[];
          status?: string;
        };
        const outputs = shellOutput.output || [];
        blocks.push(
          <div
            key={`shell-output.${shellOutput.id || index}`}
            className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700"
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Shell Output
            </div>
            {outputs.map(out => (
              <div
                key={`${out.outcome?.type}-${out.stdout?.slice(0, 20)}`}
                className="mb-2 last:mb-0"
              >
                {out.stdout && <pre className="whitespace-pre-wrap break-words">{out.stdout}</pre>}
                {out.stderr && (
                  <pre className="whitespace-pre-wrap break-words text-red-600">{out.stderr}</pre>
                )}
                {out.outcome && (
                  <div className="mt-1 text-[10px] text-gray-400">
                    {out.outcome.type === "exit"
                      ? `Exit code: ${out.outcome.exit_code ?? 0}`
                      : "Timed out"}
                  </div>
                )}
              </div>
            ))}
          </div>,
        );
      } else if (content.type === "apply_patch_call") {
        const patchBlock = content as {
          id?: string;
          call_id?: string;
          operation: { type?: string; path?: string; diff?: string };
          status?: string;
        };
        const op = patchBlock.operation || {};
        const label = op.path ? `${op.type || "patch"}: ${op.path}` : "Apply Patch";
        blocks.push(
          <CodeAccordion
            key={`patch.${patchBlock.id || index}`}
            code={op.diff || ""}
            id={patchBlock.id || `patch-${index}`}
            label={label}
            className="mb-4"
          />,
        );
      } else if (content.type === "apply_patch_call_output") {
        const patchOutput = content as {
          id?: string;
          call_id?: string;
          output?: string;
          status?: string;
        };
        blocks.push(
          <div
            key={`patch-output.${patchOutput.id || index}`}
            className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-700"
          >
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Patch Result
              {patchOutput.status && (
                <span className="ml-2 normal-case">({patchOutput.status})</span>
              )}
            </div>
            {patchOutput.output && (
              <pre className="whitespace-pre-wrap break-words">{patchOutput.output}</pre>
            )}
          </div>,
        );
      } else if (content.type === "text" && content.text) {
        const textKey = `text-${content.text.slice(0, 50).replace(/\s/g, "_")}`;

        blocks.push(
          <TextContentBlock
            key={textKey}
            content={content.text}
            highlight={highlight}
            highlightedContent={highlightedContent}
            isSingleBlock={hasSingleTextBlock}
            cacheControl={
              "cache_control" in content
                ? (content as { cache_control?: CacheControl }).cache_control
                : undefined
            }
          />,
        );
      }
    });

    if (
      message.role === "tool" &&
      "cache_control" in message &&
      (message as { cache_control?: CacheControl }).cache_control
    ) {
      const toolMsgCacheLabel = getCacheLabel(
        (message as { cache_control?: CacheControl }).cache_control,
      );
      if (toolMsgCacheLabel) {
        blocks.push(
          <div key="tool-msg-cache" className="mb-4 flex items-center gap-1">
            <BasicTooltip content="Prompt cache breakpoint (tool result)">
              <span className="flex items-center gap-0.5 rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                <DatabaseIcon className="h-3 w-3" />
                {toolMsgCacheLabel}
              </span>
            </BasicTooltip>
          </div>,
        );
      }
    }

    if (message.role === "assistant" && "tool_calls" in message && message.tool_calls) {
      for (const tc of message.tool_calls) {
        if (tc.cache_control) {
          const tcLabel = getCacheLabel(tc.cache_control);
          if (tcLabel) {
            blocks.push(
              <div key={`tc-cache-${tc.id}`} className="mb-4 flex items-center gap-1">
                <BasicTooltip content={`Prompt cache breakpoint (tool_use: ${tc.function.name})`}>
                  <span className="flex items-center gap-0.5 rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                    <DatabaseIcon className="h-3 w-3" />
                    {tcLabel}
                  </span>
                </BasicTooltip>
              </div>,
            );
          }
        }
      }
    }

    return { contentBlocks: blocks, isMalformed: false };
  }, [highlight, highlightedContent, componentIsOpen, message, allMessages]);

  const getPreviewContent = useMemo((): ReactNode => {
    if (!message?.content) {
      return null;
    }

    if (!Array.isArray(message.content)) {
      return <span className="italic text-destructive">[Malformed Request]</span>;
    }

    const mediaPart = message.content.find(
      c => c.type === "image_url" || c.type === "output_media",
    );
    if (mediaPart) {
      if (mediaPart.type === "output_media") {
        const mediaType = (mediaPart as { media_type?: string }).media_type ?? "image";
        if (mediaType === "audio") {
          return <span className="italic">[Audio]</span>;
        }
        if (mediaType === "video") {
          return <span className="italic">[Video]</span>;
        }
      }
      return <span className="italic">[Image]</span>;
    }

    const textPart = message.content.find(c => c.type === "text");
    if (textPart) {
      return truncate(textPart.text, 200);
    }

    return null;
  }, [message?.content]);

  if (!message?.content) {
    return null;
  }

  if (isMalformed) {
    return (
      <div className="group-hover:inherit pt-2 text-left italic text-gray-500">
        Malformed Request
      </div>
    );
  }

  return (
    <>
      {!componentIsOpen ? (
        <div className="group-hover:inherit pt-2 text-left">{getPreviewContent}</div>
      ) : (
        <div>
          <div className="flex flex-col gap-3">{contentBlocks}</div>
          <Citations messages={[message]} />
        </div>
      )}
    </>
  );
};
